import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { eyebrowClass, cardClass } from '@/lib/sectionStyles';

const BADGES = [
  'SOC 2 Type II',
  'SSO · RBAC · Audit Logs · Secrets Mgmt',
  'Supports all major LLMs',
];

const CAPABILITY_CARDS = [
  {
    kicker: 'Agents',
    title: 'Enterprise agents',
    description:
      'Create and deploy AI assistants, semi-autonomous agents, and autonomous agents — each with policies, controls, memory, and capabilities.',
  },
  {
    kicker: 'Workflows',
    title: 'No-code workflow builder',
    description:
      'A drag-and-drop canvas for complex, deterministic workflows — validated at save time, auditable at run time.',
  },
  {
    kicker: 'Integrations',
    title: 'Hundreds of connectors',
    description:
      'Salesforce, Slack, Google, AWS, Guidewire, databases, email, MCP servers, and a custom adapter path for everything else.',
  },
  {
    kicker: 'Enterprise-grade',
    title: 'Secure by design',
    description:
      'SSO, RBAC, rules engines, immutable audit logs, secrets management, and field-level security — built in, not bolted on.',
  },
  {
    kicker: 'Observability',
    title: 'Analytics & monitoring',
    description:
      'Real-time agent insights, performance metrics, SLA queues, and detailed execution logs across the whole fleet.',
  },
  {
    kicker: 'Governance',
    title: 'Risk-based controls',
    description:
      'Risk-tiered classification with automated approval workflows, human-in-the-loop escalation, and examination-ready evidence.',
  },
];

interface Step {
  n: string;
  title: string;
  description: ReactNode;
}

const STEPS: Step[] = [
  {
    n: '01',
    title: 'Create enterprise agents',
    description:
      'The agent wizard guides you through four phases — Define, Build, Govern, Validate — so identity, purpose, operational boundaries, policies, and approvals are set before anything ships. Start from the pre-built library or from scratch.',
  },
  {
    n: '02',
    title: 'Add skills and workflows',
    description:
      'An AI assistant helps generate reusable skills, inject organizational best practices, compose multi-step workflows, and wire integrations — so agents inherit your knowledge, not just your prompts.',
  },
  {
    n: '03',
    title: 'Deploy in the right operating mode',
    description: (
      <>
        Every agent runs in one of three modes:{' '}
        <strong className="text-foreground">Assistant</strong> (advises and supports human
        decision-makers), <strong className="text-foreground">Semi-Autonomous</strong> (executes the
        routine, escalates the exceptional), or{' '}
        <strong className="text-foreground">Autonomous</strong> (operates independently within
        policy-bounded limits). The mode is a governance decision, not a model setting.
      </>
    ),
  },
  {
    n: '04',
    title: 'Coordinate the fleet with the Brain',
    description:
      'The Brain Coordinator is the per-customer intelligence layer that balances workloads across agent pools, guides decisions with rules and AI, orchestrates multi-agent processes at enterprise scale, and enforces policies, compliance, and SLAs — one isolated coordinator per customer.',
  },
];

const FEATURES = [
  {
    lead: 'Where people meet agents:',
    rest: 'the mesh routes conversations across Slack, Microsoft Teams, Google Chat, and the in-app panel — approvals arrive where the approver already works.',
  },
  {
    lead: 'Open protocols:',
    rest: 'A2A is the backbone for agent-to-agent communication; MCP servers are first-class governed citizens.',
  },
  {
    lead: 'Per-customer isolation:',
    rest: 'every customer gets a dedicated coordinator with its own database, cache, and storage. Provisioning tiers add a dedicated cloud project, customer-managed keys, and a private-IP data plane.',
  },
];

export function PlatformPage() {
  return (
    <>
      <Helmet>
        <title>Platform - CriticalBridge.AI</title>
        <meta
          name="description"
          content="CriticalBridge overlays your existing infrastructure — core systems, identity, data, models — with a governed agentic layer. Build, deploy, and govern AI agents and multi-step workflows within your security perimeter, without replatforming anything."
        />
        <meta
          name="keywords"
          content="agentic ai platform, ai agent orchestration, ai governance platform, no-code workflow builder"
        />
        <meta property="og:title" content="Platform - CriticalBridge.AI" />
        <meta
          property="og:description"
          content="An intelligent orchestration layer over the enterprise you already run."
        />
        <link rel="canonical" href="https://www.criticalbridge.ai/platform" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Platform
          </div>
          <h1 className="mt-4 max-w-[26ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl">
            An intelligent orchestration layer over the enterprise you already run.
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            CriticalBridge overlays your existing infrastructure — core systems, identity, data,
            models — with a governed agentic layer. Build, deploy, and govern AI agents and
            multi-step workflows within your security perimeter, without replatforming anything.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {BADGES.map((badge) => (
              <span
                key={badge}
                className="rounded-full border border-secondary-foreground/30 px-3.5 py-1.5 font-mono text-xs text-secondary-foreground/70"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Everything you need */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>Everything you need</span>
          <h2 className="mt-2 text-3xl font-bold">Agents, workflows, and governance — unified.</h2>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
            Enterprise AI teams shouldn't need separate tools to build agents, design workflows,
            deploy integrations, and satisfy compliance. CriticalBridge is one environment for all
            four.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITY_CARDS.map((card) => (
              <div key={card.title} className={cardClass}>
                <span className="font-mono text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {card.kicker}
                </span>
                <h3 className="mt-1 text-lg font-bold">{card.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-y bg-card py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>How it works</span>
          <h2 className="mt-2 text-3xl font-bold">
            From zero to governed automation in four steps.
          </h2>

          <div className="mt-8 divide-y divide-border">
            {STEPS.map((step) => (
              <div
                key={step.n}
                className="grid grid-cols-[56px_1fr] gap-4 py-6 first:pt-0 sm:grid-cols-[64px_1fr]"
              >
                <div className="font-display text-2xl font-extrabold text-primary">{step.n}</div>
                <div>
                  <h3 className="text-lg font-bold">{step.title}</h3>
                  <p className="mt-1.5 max-w-[64ch] text-[15.5px] leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Under the hood */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>Under the hood</span>
          <h2 className="mt-2 text-3xl font-bold">Even the model choice is governed.</h2>
          <p className="mt-4 max-w-[68ch] text-muted-foreground">
            A governed model router sits under every agent call: per-entity allowlists and
            denylists, data-residency constraints, quality tiers and cost modes, health-aware
            failover, and cascade escalation when a model underperforms — with an immutable,
            queryable audit answering "why this model" for every routed call. Route work across
            frontier APIs and open-source models under one policy. LLM cost control is a governance
            feature, not an afterthought.
          </p>

          <ul className="mt-4 grid max-w-[74ch] gap-2.5">
            {FEATURES.map((feature) => (
              <li key={feature.lead} className="relative pl-6 text-base text-muted-foreground">
                <span className="absolute left-0 top-2 h-2.5 w-2.5 rounded-sm bg-primary" />
                <strong className="text-foreground">{feature.lead}</strong> {feature.rest}
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button variant="outline" asChild>
              <Link to="/trust">Governance & trust →</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/studio">Build with Studio →</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
